import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'components/common/Button';
import { apiClient } from 'services/api-client';
import { AuthService } from 'services/auth/auth.service';
import { isSupabaseAuthConfigured, supabase } from 'services/auth/supabase.client';

interface InvitationInfo {
  email: string;
  role: string;
  organization_name?: string;
  organizations?: { name: string };
  invited_by_name?: string;
  expires_at: string;
}

export default function AcceptInvitePage() {
  const token = new URLSearchParams(useLocation().search).get('token') || '';
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [mode, setMode] = useState<'register' | 'login'>('register');
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const payload = await apiClient.get<InvitationInfo>(`/invitations/${token}`);
        setInvitation({
          ...payload.data,
          organization_name: payload.data.organization_name || payload.data.organizations?.name,
        });
        setForm(current => ({ ...current, email: payload.data.email }));
        const session = await supabase?.auth.getSession();
        if (session?.data.session) setMode('register');
      } catch (loadError: any) { setError(loadError.message); }
      finally { setLoading(false); }
    };
    if (token) void load(); else { setError('Invitation token is missing'); setLoading(false); }
  }, [token]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setError('');
    if (!isSupabaseAuthConfigured || !supabase) { setError('Supabase Auth is not configured.'); return; }
    if (mode === 'register' && form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      if (mode === 'register') {
        const session = (await supabase.auth.getSession()).data.session;
        if (!session) { setError('Open this page from the Supabase invitation email.'); return; }
        const result = await supabase.auth.updateUser({ password: form.password, data: { full_name: form.name } });
        if (result.error) throw result.error;
      } else {
        const result = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
        if (result.error) throw result.error;
      }
      await AuthService.acceptInvitation(token);
      navigate('/dashboard');
    } catch (submitError: any) { setError(submitError.message || 'Unable to accept invitation.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Loading invitation…</div>;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <img src="/PellvrLogotrans.png" alt="Pellvr" className="h-10 w-auto object-contain object-left" />
        <h1 className="mt-3 text-xl font-bold text-blue-900">Join Pellvr</h1>
        {error && <div className="mt-4 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}
        {invitation && <><p className="mt-3 text-sm text-gray-600">You’ve been invited to join <strong>{invitation.organization_name}</strong> as a <strong>{invitation.role}</strong>.</p><form onSubmit={submit} className="mt-5 space-y-4">{mode === 'register' && <div><label className="block text-sm font-medium text-gray-700 mb-1">Full name</label><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div>}<div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Password</label><input required type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div>{mode === 'register' && <div><label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label><input required type="password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div>}<Button loading={submitting} type="submit" className="w-full justify-center">{mode === 'register' ? 'Create account and join' : 'Sign in and join'}</Button></form><button onClick={() => setMode(mode === 'register' ? 'login' : 'register')} className="mt-4 w-full text-xs text-blue-600 hover:underline">{mode === 'register' ? 'Already have an account? Sign in' : 'Need an account? Create one'}</button></>}
      </div>
    </div>
  );
}
